import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, Swords, Store, ChevronRight, ChevronLeft, X } from 'lucide-react';

const slides = [
  {
    id: 1,
    title: 'DEPOSIT $SLAP',
    subtitle: 'Fund Your Wallet',
    description: 'Tap your wallet balance to deposit SOL and swap for $SLAP tokens. Use the built-in Jupiter swap with 1% platform fee.',
    icon: Wallet,
    gradient: 'from-yellow-500 to-orange-500',
    tips: ['Swap SOL → $SLAP instantly', 'Min deposit: Any amount', 'Platform fee: 1%'],
  },
  {
    id: 2,
    title: 'PLAY TO EARN',
    subtitle: 'Enter the Arena',
    description: 'Join the SLAP BATTLES arena! Entry fee is 100 $SLAP. Winner takes the pot minus 10% dev tax.',
    icon: Swords,
    gradient: 'from-red-500 to-pink-500',
    tips: ['Entry: 100 $SLAP', 'Winner takes 90%', '30 seconds of chaos'],
  },
  {
    id: 3,
    title: 'TRADE CARDS',
    subtitle: 'P2P Marketplace',
    description: 'Open packs to get rare cards. Sell them on the marketplace for $SLAP. Prices set by you, 5% creator fee.',
    icon: Store,
    gradient: 'from-purple-500 to-cyan-500',
    tips: ['Basic Pack: 100 $SLAP', 'Premium Pack: 500 $SLAP', 'Sell for profit'],
  },
];

const OnboardingCarousel = ({ isOpen, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const slide = slides[currentSlide];
  const Icon = slide.icon;
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <motion.div
      className="fixed inset-0 z-[10002] bg-black/95 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="onboarding-carousel"
    >
      {/* EMERGENCY FIX: Skip Button with z-index 99999 - ALWAYS CLICKABLE */}
      <button
        onClick={handleSkip}
        className="absolute top-4 right-4 p-3 px-5 bg-red-500/30 hover:bg-red-500/60 rounded-full transition-colors text-white text-sm font-bold flex items-center gap-2 cursor-pointer"
        style={{ zIndex: 99999 }}
        data-testid="carousel-skip-btn"
      >
        SKIP <X className="w-5 h-5" />
      </button>

      {/* Progress Dots */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((_, idx) => (
          <div
            key={idx}
            className={`w-3 h-3 rounded-full transition-all ${
              idx === currentSlide 
                ? 'bg-white scale-110' 
                : idx < currentSlide 
                  ? 'bg-green-500' 
                  : 'bg-white/30'
            }`}
          />
        ))}
      </div>

      <div className="max-w-md w-full">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            {/* Icon */}
            <motion.div
              className={`w-28 h-28 mx-auto mb-8 rounded-3xl bg-gradient-to-br ${slide.gradient} flex items-center justify-center shadow-2xl`}
              animate={{ 
                boxShadow: [
                  `0 0 30px ${slide.gradient.includes('yellow') ? 'rgba(234,179,8,0.4)' : slide.gradient.includes('red') ? 'rgba(239,68,68,0.4)' : 'rgba(168,85,247,0.4)'}`,
                  `0 0 60px ${slide.gradient.includes('yellow') ? 'rgba(234,179,8,0.6)' : slide.gradient.includes('red') ? 'rgba(239,68,68,0.6)' : 'rgba(168,85,247,0.6)'}`,
                  `0 0 30px ${slide.gradient.includes('yellow') ? 'rgba(234,179,8,0.4)' : slide.gradient.includes('red') ? 'rgba(239,68,68,0.4)' : 'rgba(168,85,247,0.4)'}`,
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Icon className="w-14 h-14 text-white" />
            </motion.div>

            {/* Step Number */}
            <p className="text-white/40 text-sm font-bold mb-2">
              STEP {currentSlide + 1} OF {slides.length}
            </p>

            {/* Title */}
            <h2 
              className="text-4xl sm:text-5xl font-black mb-2"
              style={{
                background: `linear-gradient(135deg, ${slide.gradient.includes('yellow') ? '#FFD700, #FF6600' : slide.gradient.includes('red') ? '#FF0000, #FF00FF' : '#A855F7, #00FFFF'})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {slide.title}
            </h2>

            {/* Subtitle */}
            <p className="text-white/60 text-lg mb-6">{slide.subtitle}</p>

            {/* Description */}
            <p className="text-white/80 text-sm mb-8 leading-relaxed">
              {slide.description}
            </p>

            {/* Tips */}
            <div className="bg-white/5 rounded-2xl p-4 mb-8 border border-white/10">
              <div className="grid grid-cols-1 gap-2">
                {slide.tips.map((tip, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-2 text-left text-sm text-white/70"
                  >
                    <span className="text-green-400">✓</span>
                    {tip}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {currentSlide > 0 && (
            <motion.button
              onClick={handlePrev}
              className="flex-1 py-4 bg-white/10 hover:bg-white/20 rounded-2xl font-bold text-white flex items-center justify-center gap-2 transition-colors"
              whileTap={{ scale: 0.98 }}
              data-testid="carousel-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              BACK
            </motion.button>
          )}
          
          <motion.button
            onClick={handleNext}
            className={`flex-1 py-4 rounded-2xl font-black text-xl text-white flex items-center justify-center gap-2 ${
              isLastSlide 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-lg shadow-green-500/30' 
                : 'bg-gradient-to-r from-purple-600 to-cyan-600'
            }`}
            whileTap={{ scale: 0.98 }}
            data-testid="carousel-next-btn"
          >
            {isLastSlide ? 'START PLAYING' : 'NEXT'}
            {!isLastSlide && <ChevronRight className="w-5 h-5" />}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default OnboardingCarousel;
